import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { getFormNotification } from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    loadHistoricalValuesFromBackend,
    loadHistoricalValuesFromBackendSuccess,
} from '../actions/historical-values.actions';
import { BsModalService } from 'ngx-bootstrap/modal';
import { catchError, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { LogInstanceToken } from '@evorait/pace-logging';
import { of } from 'rxjs';
import { HistoricalValuesService } from '../../services/historical-values.service';
import { getFormId } from '../selectors/form.selector';
import { LoadingScreenComponent, LoadingScreenModel, ModalClass } from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root',
})
export class HistoricalValuesEffectsService {
    private readonly store = inject(Store<FormsState>);
    private readonly actions$ = inject(Actions);
    private readonly modalService = inject(BsModalService);
    private readonly log = inject(LogInstanceToken);
    private readonly historicalValuesService = inject(HistoricalValuesService);
    private readonly translate = inject(TranslateService);

    public loadHistoricalValuesFromBackend$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadHistoricalValuesFromBackend),
            withLatestFrom(this.store.select(getFormId), this.store.select(getFormNotification)),
            switchMap(([action, formId, notificationId]) => {
                const loadingScreenModel = new LoadingScreenModel(
                    <string>this.translate.instant('HISTORICAL_VALUES_LOADING'),
                );

                const loadingScreenInstance = this.modalService.show(LoadingScreenComponent, {
                    backdrop: 'static',
                    class: ModalClass.MODAL_SMALL_NO_FULLSCREEN,
                    initialState: {
                        model: loadingScreenModel,
                    },
                    ariaLabelledBy: 'loading-screen-modal-title',
                });

                return loadingScreenModel.loadingScreenDisplayed$.pipe(
                    take(1),
                    switchMap(() =>
                        this.historicalValuesService.loadAllHistoricalValuesForUniqueId(
                            action.uniqueId,
                            notificationId,
                            formId.formId,
                        ),
                    ),
                    map((historicalValues) => loadHistoricalValuesFromBackendSuccess({ historicalValues })),
                    tap(() => loadingScreenInstance.hide()),
                    catchError((error) => {
                        this.log.error(
                            `HistoricalValuesEffectsService: loadHistoricalValuesFromBackend$: Error while loading historical values ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        loadingScreenInstance.hide();
                        return of(error);
                    }),
                );
            }),
        );
    });
}
